import {AgentApprovalCategory, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';

import {AssistantEffort, CitationGenerationMode} from './ChatDAO';
import {
  StateUpdateChatEventNode,
  RequestStateUpdateChatEventNode,
  DisplayMessageChatEventNode,
  ClientChatEvent,
} from './messages/MessageTypes';

interface ChatSystemState {
  allowed_approval_categories?: Array<AgentApprovalCategory>;
  error_message?: string;
}

function getLatestStateUpdateEvent(events: Array<ClientChatEvent>): StateUpdateChatEventNode | undefined {
  return events.findLast(
    (event): event is StateUpdateChatEventNode => event.__typename === 'StateUpdateChatEventNode'
  );
}

function getLatestRequestStateUpdateEvent(
  events: Array<ClientChatEvent>
): RequestStateUpdateChatEventNode | undefined {
  return events.findLast(
    (event): event is RequestStateUpdateChatEventNode =>
      event.__typename === 'RequestStateUpdateChatEventNode'
  );
}

function getLatestUserDisplayMessageEvent(
  events: Array<ClientChatEvent>
): DisplayMessageChatEventNode | undefined {
  return events.findLast(
    (event): event is DisplayMessageChatEventNode =>
      event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.USER
  );
}

export function getLatestApplicationState(events: Array<ClientChatEvent>): object | null {
  const stateUpdateEvent = getLatestStateUpdateEvent(events);
  return stateUpdateEvent?.newState.chat_state.application_state ?? null;
}

export function getLatestSystemState(events: Array<ClientChatEvent>): ChatSystemState | null {
  const stateUpdateEvent = getLatestStateUpdateEvent(events);
  return stateUpdateEvent?.newState.chat_state.system_state ?? null;
}

export function hasChatScopedWriteApproval(events: Array<ClientChatEvent>): boolean {
  return (
    getLatestSystemState(events)?.allowed_approval_categories?.includes(
      AgentApprovalCategory.WRITE_ACTIONS
    ) ?? false
  );
}

/**
 * Whether "full access" should be shown for display purposes (the chip and plus-menu selection),
 * as opposed to hasChatScopedWriteApproval's strict WRITE_ACTIONS-only semantics.
 *
 * A workspace folder alone (with no WRITE_ACTIONS) also confers "full access" from the user's
 * perspective: background agents/routines create chats scoped to a folder, and the agent can write
 * there without asking. Do NOT use this for AgentApprovalCard's "Allow" vs "Allow once" choice - an
 * approval request in a folder-scoped chat is precisely a request to act *outside* the workspace,
 * where granting WRITE_ACTIONS for the rest of the chat is the meaningful choice. That must keep
 * using hasChatScopedWriteApproval directly.
 *
 * `agentWorkspaceFolderId` should be the folder ID regardless of agent type (see
 * getAgentWorkspaceFolderId) - background agents/routines aren't limited to co-scientist.
 * `approvalsEnabled` gates the whole thing: when per-user approvals aren't enabled (even if
 * released for the tenant), this must return false so the UI doesn't show "full access" the user
 * has no way to grant or revoke. This makes the same folder-scoped chat read as "full access" for
 * one user and "default access" for another, purely based on their approvals config - a deliberate
 * framing difference (not a bug) that's needed so a user without approvals enabled still sees their
 * folder (e.g. in AI Scientist mode) without it being mislabeled as full access they can't manage.
 */
export function hasFullAccessForDisplay(
  events: Array<ClientChatEvent>,
  {
    agentWorkspaceFolderId,
    approvalsEnabled,
  }: {agentWorkspaceFolderId: string | null; approvalsEnabled: boolean}
): boolean {
  if (!approvalsEnabled) {
    return false;
  }
  return hasChatScopedWriteApproval(events) || agentWorkspaceFolderId != null;
}

/**
 * Get the publicDataEnabled setting from the last RequestStateUpdateChatEventNode.
 * Falls back to false if not found.
 */
export function getLatestPublicDataEnabled(events: Array<ClientChatEvent>): boolean {
  const requestStateUpdateEvent = getLatestRequestStateUpdateEvent(events);
  return requestStateUpdateEvent?.publicDataEnabled ?? false;
}

/**
 * Get the citationGenerationMode setting from the last user DisplayMessageChatEventNode.
 * The mode is persisted per-message on the user's display event. Falls back to AUTO if not found.
 */
export function getLatestCitationGenerationMode(events: Array<ClientChatEvent>): CitationGenerationMode {
  const userDisplayEvent = getLatestUserDisplayMessageEvent(events);
  const citationGenerationMode = userDisplayEvent?.citationGenerationMode;
  if (
    citationGenerationMode != null &&
    Object.values(CitationGenerationMode).includes(citationGenerationMode)
  ) {
    return citationGenerationMode;
  }
  return CitationGenerationMode.AUTO;
}

export function getLatestEffort(events: Array<ClientChatEvent>): AssistantEffort | null {
  const effort = getLatestUserDisplayMessageEvent(events)?.effort;
  return effort === AssistantEffort.QUICK || effort === AssistantEffort.STANDARD ? effort : null;
}
